import { askConfirm } from '../components/ask';
import { phoneField } from '../phone';
import { toast } from '../components/toast';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get, post, put } from '../api';
import { useAuth } from '../auth';
import { brandChanged } from '../components/Brand';
import { Problems } from '../components/Field';
import { Row } from '../components/FormRow';
import { SectionTabs } from '../components/SectionTabs';
import { dayLabel } from '../dates';
import { hoursOf, minutesFrom } from '../duration';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { Activity } from './Activity';
import { AuditSettings } from './AuditSettings';
import { Deactivated } from './Deactivated';
import { MessagingSettings } from './MessagingSettings';
import { SystemStatus } from './SystemStatus';

interface SettingsResponse {
  business: { name: string; slug: string; timezone: string };
  /** Whether this business's invoices go to Xero: decided per business by Teknika, not here. */
  uses_xero: boolean;
  settings: Record<string, string | number | Record<string, string>>;
  fetched_holidays: { date: string; name: string }[];
  holidays_fetched_at: string;
  holidays_error: string;
}
interface XeroStatus {
  configured: boolean;
  connected: boolean;
  client_id: string;
  organisation: string;
  has_webhook_key: boolean;
  webhook_url: string;
  redirect_uri: string;
}

const XERO_NOTICES: Record<string, string> = {
  connected: 'Xero is connected.',
  declined: 'Xero access was declined, so nothing changed.',
  failed: 'The Xero connection did not complete. Please try connecting again.',
};

/** Tabs marked `owner` touch billing, tax codes, or third-party credentials: owner-only. */
const TABS = [
  { key: 'general', label: 'General', owner: true },
  { key: 'invoicing', label: 'Invoicing', owner: true },
  { key: 'integrations', label: 'Integrations', owner: true },
  { key: 'messaging', label: 'Email & SMS', owner: true },
  { key: 'deactivated', label: 'Deactivated', owner: false },
  { key: 'activity', label: 'Activity', owner: false },
  { key: 'audit', label: 'Change history', owner: true },
  { key: 'status', label: 'System status', owner: false },
];

const AU_STATES: [string, string][] = [
  ['ACT', 'Australian Capital Territory'],
  ['NSW', 'New South Wales'],
  ['NT', 'Northern Territory'],
  ['QLD', 'Queensland'],
  ['SA', 'South Australia'],
  ['TAS', 'Tasmania'],
  ['VIC', 'Victoria'],
  ['WA', 'Western Australia'],
];

export function Settings() {
  const { user } = useAuth();
  const canManageSettings = user?.capabilities.includes('teknika_manage_settings') ?? false;
  const tabs = TABS.filter((t) => canManageSettings || !t.owner);

  const [params] = useSearchParams();
  // A return from Xero lands on ?xero=..., which belongs on Integrations; so do
  // links to the old Xero tab. Links to the old Email tab go to Email & SMS.
  const asked = params.get('tab') ?? (params.get('xero') ? 'integrations' : 'general');
  const wanted = asked === 'xero' ? 'integrations' : asked === 'email' ? 'messaging' : asked;
  // Falls back to a tab this account can actually see, so a bookmarked or
  // shared link to an owner-only tab never shows a bare 403 instead.
  const tab = tabs.some((t) => t.key === wanted) ? wanted : (tabs[0]?.key ?? '');
  const notice = XERO_NOTICES[params.get('xero') ?? ''];
  return (
    <>
      <h1>Settings</h1>
      <SectionTabs
        label="Settings sections"
        tabs={tabs}
        current={tab}
        to={(k) => `/settings?tab=${k}`}
      />
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {tab === 'general' && <GeneralSettings />}
      {tab === 'invoicing' && <InvoicingSettings />}
      {tab === 'integrations' && <Integrations />}
      {tab === 'messaging' && <MessagingSettings />}
      {tab === 'deactivated' && <Deactivated />}
      {tab === 'activity' && <Activity />}
      {tab === 'audit' && <AuditSettings />}
      {tab === 'status' && <SystemStatus />}
    </>
  );
}

/** Load the settings once, and hand them to a form that sends back only its own fields. */
function useSettings() {
  const [tick, setTick] = useState(0);
  const loaded = useLoad(() => get<SettingsResponse>('/api/settings'), [tick]);
  return { ...loaded, reload: () => setTick((t) => t + 1) };
}

// ---- General ----

const NUMBERS: [string, string][] = [
  ['visit_horizon_weeks', 'Create visits this many weeks ahead'],
  ['offer_window_days', 'How far ahead contractors can browse offered work (days)'],
];

function GeneralSettings() {
  const loaded = useSettings();
  if (loaded.loading && !loaded.data) return <p>Loading…</p>;
  if (loaded.error || !loaded.data) {
    return (
      <p role="alert" className="error">
        {loaded.error}
      </p>
    );
  }
  return <GeneralForm initial={loaded.data} reload={loaded.reload} />;
}

function GeneralForm(props: { initial: SettingsResponse; reload(): void }) {
  const s0 = props.initial.settings;
  const [num, setNum] = useState<Record<string, number>>(
    Object.fromEntries(NUMBERS.map(([k]) => [k, Number(s0[k] ?? 0)])),
  );
  // Kept in minutes, typed in hours.
  const [visitHours, setVisitHours] = useState(hoursOf(Number(s0.default_visit_duration ?? 0)));
  const [timezone, setTimezone] = useState(props.initial.business.timezone);
  // Set per business when it is created, so read here but not edited.
  const country = String(s0.country ?? '');
  const [region, setRegion] = useState(String(s0.holiday_region ?? ''));
  const [phone, setPhone] = useState(String(s0.office_phone ?? ''));
  const [seesPay, setSeesPay] = useState(Number(s0.cleaner_sees_own_pay) === 1);
  const [holidays, setHolidays] = useState<{ date: string; name: string }[]>(
    Object.entries((s0.public_holidays as Record<string, string>) ?? {}).map(([date, name]) => ({
      date,
      name,
    })),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [fetching, setFetching] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setFields({});
    try {
      await put('/api/settings', {
        timezone,
        settings: {
          ...num,
          default_visit_duration: minutesFrom(visitHours),
          holiday_region: region,
          office_phone: phone,
          cleaner_sees_own_pay: seesPay ? 1 : 0,
          public_holidays: Object.fromEntries(
            holidays.filter((h) => h.date && h.name.trim()).map((h) => [h.date, h.name.trim()]),
          ),
        },
      });
      toast('Saved.');
      brandChanged();
      props.reload();
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    }
  }

  async function fetchNow() {
    setFetching('');
    try {
      await post('/api/settings/holidays/refresh');
      setFetching('Fetched the latest public holidays.');
      props.reload();
    } catch (err) {
      setFetching(describe(err).problems[0] ?? 'Could not fetch holidays.');
    }
  }

  const setHoliday = (i: number, patch: Partial<{ date: string; name: string }>) =>
    setHolidays(holidays.map((h, n) => (n === i ? { ...h, ...patch } : h)));

  return (
    <form onSubmit={submit} className="stack narrow" noValidate>
      <Problems problems={problems} />
      <section className="panel" aria-label="General">
        {NUMBERS.map(([key, label]) => (
          <Row key={key} label={label}>
            <input
              inputMode="numeric"
              aria-label={label}
              className="short"
              value={String(num[key] ?? '')}
              onChange={(e) => setNum({ ...num, [key]: Number(e.target.value) || 0 })}
            />
          </Row>
        ))}
        <Row label="Default visit length (hours)">
          <input
            inputMode="decimal"
            aria-label="Default visit length (hours)"
            className="short"
            value={visitHours}
            onChange={(e) => setVisitHours(e.target.value)}
          />
        </Row>
        <Row label="Timezone" error={fields.timezone}>
          <input
            aria-label="Timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          />
        </Row>
        <Row label="Public holidays" wide>
          <div className="inline">
            <label className="inline-label">
              State or region
              {country.toUpperCase() === 'AU' ? (
                <select
                  aria-label="Public holiday region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                >
                  <option value="">National days only</option>
                  {AU_STATES.map(([code, name]) => (
                    <option key={code} value={`AU-${code}`}>
                      {name}
                    </option>
                  ))}
                  {region !== '' && !AU_STATES.some(([c]) => `AU-${c}` === region) && (
                    <option value={region}>{region}</option>
                  )}
                </select>
              ) : (
                <input
                  aria-label="Public holiday region (e.g. GB-ENG)"
                  className="short"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                />
              )}
            </label>
            <button type="button" className="quiet" onClick={() => void fetchNow()}>
              Fetch now
            </button>
          </div>
          {fetching && <span role="status">{fetching}</span>}
          {props.initial.holidays_error && (
            <span className="error">The last fetch failed: {props.initial.holidays_error}</span>
          )}
          {props.initial.fetched_holidays.length > 0 && (
            <details>
              <summary>
                {props.initial.fetched_holidays.length} from the state feed
                {props.initial.holidays_fetched_at
                  ? ` (fetched ${dayLabel(props.initial.holidays_fetched_at.slice(0, 10))})`
                  : ''}
              </summary>
              <ul className="plain">
                {props.initial.fetched_holidays.map((h) => (
                  <li key={h.date}>
                    {dayLabel(h.date)} — {h.name}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <strong className="sub-head">Your own dates</strong>
          {holidays.map((h, i) => (
            <div key={i} className="inline">
              <input
                type="date"
                aria-label={`Holiday ${i + 1} date`}
                value={h.date}
                onChange={(e) => setHoliday(i, { date: e.target.value })}
              />
              <input
                aria-label={`Holiday ${i + 1} name`}
                value={h.name}
                onChange={(e) => setHoliday(i, { name: e.target.value })}
              />
              <button
                type="button"
                className="danger"
                onClick={() => setHolidays(holidays.filter((_, n) => n !== i))}
              >
                Remove<span className="sr-only"> holiday {i + 1}</span>
              </button>
            </div>
          ))}
          {fields.public_holidays && <span className="error">{fields.public_holidays}</span>}
          <button
            type="button"
            className="quiet add-tag"
            onClick={() => setHolidays([...holidays, { date: '', name: '' }])}
          >
            + Add holiday
          </button>
        </Row>
        <Row
          label="Office sign-in link"
          hint="Bookmark or share this with your office staff: it signs in as this business, with no business name to type."
        >
          <code>{`${window.location.origin}/login/${props.initial.business.slug}`}</code>
        </Row>
        <Row label="Portal address" hint="Contractors sign in at /portal with this business name.">
          <code>{props.initial.business.slug}</code>
        </Row>
        <Row label="Office phone" hint="Shown to contractors so they know who to ring.">
          <input
            aria-label="Office phone"
            value={phone}
            onChange={(e) => setPhone(phoneField(e.target.value))}
          />
        </Row>
        <Row label="Contractors see their own pay">
          <label className="check">
            <input
              type="checkbox"
              checked={seesPay}
              onChange={(e) => setSeesPay(e.target.checked)}
            />
            Show pay on the portal for visits they lead
          </label>
        </Row>
      </section>
      <div className="row start">
        <button type="submit">Save changes</button>
      </div>
    </form>
  );
}

// ---- Invoicing ----

function InvoicingSettings() {
  const loaded = useSettings();
  if (loaded.loading && !loaded.data) return null;
  if (!loaded.data) return null;
  return <InvoicingForm initial={loaded.data.settings} usesXero={loaded.data.uses_xero} />;
}

/** For a business without Xero: what its own invoices carry, so they are complete and say how to pay. */
const OWN_INVOICE: [string, string, string][] = [
  ['invoice_prefix', 'Invoice number prefix', 'Invoices are numbered INV-0001, INV-0002 and on.'],
  ['business_abn', 'ABN', 'A tax invoice needs it.'],
  ['bank_account_name', 'Bank account name', ''],
  ['bank_bsb', 'BSB', ''],
  ['bank_account_number', 'Account number', ''],
];

function InvoicingForm(props: { initial: SettingsResponse['settings']; usesXero: boolean }) {
  const [s, setS] = useState<Record<string, string>>(
    Object.fromEntries(
      [
        'default_billing_cycle',
        'account_code_income',
        'account_code_expense',
        'tax_type_income',
        'tax_type_expense',
        'line_amount_types',
        'business_address',
        ...OWN_INVOICE.map(([k]) => k),
      ].map((k) => [k, String(props.initial[k] ?? '')]),
    ),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const text = (key: string) => ({
    value: s[key] ?? '',
    onChange: (e: { target: { value: string } }) => setS({ ...s, [key]: e.target.value }),
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    try {
      await put('/api/settings', { settings: s });
      toast('Saved.');
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  return (
    <form onSubmit={submit} className="stack narrow" noValidate>
      <Problems problems={problems} />
      <section className="panel" aria-label="Invoicing">
        <Row label="Default billing cycle">
          <select aria-label="Default billing cycle" {...text('default_billing_cycle')}>
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="four_weekly">Every 4 weeks</option>
            <option value="monthly">Monthly</option>
          </select>
        </Row>
        {props.usesXero && (
          <>
            <Row label="Income account code">
              <input aria-label="Income account code" {...text('account_code_income')} />
            </Row>
            <Row label="Expense account code">
              <input aria-label="Expense account code" {...text('account_code_expense')} />
            </Row>
            <Row label="Income tax type">
              <input aria-label="Income tax type" {...text('tax_type_income')} />
            </Row>
            <Row label="Expense tax type">
              <input aria-label="Expense tax type" {...text('tax_type_expense')} />
            </Row>
            <Row label="Amounts are">
              <select aria-label="Amounts are" {...text('line_amount_types')}>
                <option value="Exclusive">Exclusive of tax</option>
                <option value="Inclusive">Inclusive of tax</option>
                <option value="NoTax">No tax</option>
              </select>
            </Row>
          </>
        )}
      </section>
      {!props.usesXero && (
        <section className="panel" aria-labelledby="own-invoice-h">
          <h2 id="own-invoice-h">On your invoices</h2>
          <p className="muted">
            Invoices are kept in this app and sent by you. These details go on each one, with your
            logo and phone from General.
          </p>
          {OWN_INVOICE.map(([key, label, hint]) => (
            <Row key={key} label={label} hint={hint || undefined}>
              <input aria-label={label} {...text(key)} />
            </Row>
          ))}
          <Row label="Business address">
            <textarea aria-label="Business address" rows={3} {...text('business_address')} />
          </Row>
        </section>
      )}
      <div className="row start">
        <button type="submit">Save invoicing settings</button>
      </div>
    </form>
  );
}

// ---- Integrations ----

/** Xero is offered to a business Teknika has set up to use it; any other keeps its invoices here. */
function Integrations() {
  const loaded = useSettings();
  if (loaded.loading && !loaded.data) return null;
  if (loaded.data && !loaded.data.uses_xero) {
    return (
      <p className="empty">
        This business keeps its invoices in this app, so there is nothing to connect. To use Xero
        instead, ask Teknika to set it up.
      </p>
    );
  }
  return (
    <>
      <XeroSettings />
      <XeroContactImport />
    </>
  );
}

// ---- Xero connection ----

function XeroSettings() {
  const [tick, setTick] = useState(0);
  const [clientId, setClientId] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [hook, setHook] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const status = useLoad(() => get<XeroStatus>('/api/accounting/xero'), [tick]);
  const x = status.data;

  async function run(fn: () => Promise<unknown>, after?: () => void) {
    setProblems([]);
    try {
      await fn();
      after?.();
      setTick((t) => t + 1);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    await run(
      () =>
        put('/api/accounting/xero', {
          client_id: clientId ?? x?.client_id ?? '',
          client_secret: secret,
          webhook_key: hook,
        }),
      () => {
        setSecret('');
        setHook('');
        toast('Saved.');
      },
    );
  }

  async function connect() {
    setProblems([]);
    try {
      const r = await post<{ url: string }>('/api/accounting/xero/connect');
      window.location.assign(r.url);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  // Keep the section on screen while it reloads after a save or connect, so it does not blink out.
  if (status.loading && !x) return null;
  if (status.error || !x) {
    return (
      <p role="alert" className="error">
        {status.error || 'Xero settings are not available on this server.'}
      </p>
    );
  }

  return (
    <section className="stack narrow" aria-labelledby="xero-h">
      <h2 id="xero-h">Xero</h2>
      <p>
        {x.connected
          ? `Connected to ${x.organisation || 'your Xero organisation'}.`
          : 'Not connected. Each business uses its own Xero app: enter its details, then connect.'}
      </p>
      <Problems problems={problems} title="Could not do that." />
      <form onSubmit={save} className="stack" noValidate>
        <section className="panel" aria-label="Your Xero app">
          <Row label="Client ID">
            <input
              aria-label="Client ID"
              value={clientId ?? x.client_id}
              onChange={(e) => setClientId(e.target.value)}
            />
          </Row>
          <Row label="Client secret">
            <input
              type="password"
              autoComplete="off"
              aria-label={x.configured ? 'Client secret (leave blank to keep)' : 'Client secret'}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </Row>
          <Row label="Webhook key">
            <input
              type="password"
              autoComplete="off"
              aria-label={x.has_webhook_key ? 'Webhook key (leave blank to keep)' : 'Webhook key'}
              value={hook}
              onChange={(e) => setHook(e.target.value)}
            />
          </Row>
        </section>
        <p className="muted">
          In your Xero app, set the redirect URI to <code>{x.redirect_uri}</code> and the webhook
          URL to <code>{x.webhook_url}</code>.
        </p>
        <div className="row start">
          <button type="submit">Save Xero details</button>
          {x.configured && !x.connected && (
            <button type="button" onClick={() => void connect()}>
              Connect to Xero
            </button>
          )}
          {x.connected && (
            <button
              type="button"
              className="danger"
              onClick={() => {
                void askConfirm({
                  title: 'Disconnect Xero?',
                  body: 'Invoices already created stay in Xero.',
                  confirmLabel: 'Disconnect',
                  danger: true,
                }).then((yes) => {
                  if (yes) void run(() => post('/api/accounting/xero/disconnect'));
                });
              }}
            >
              Disconnect
            </button>
          )}
        </div>
      </form>
      <p className="muted">
        Invoices are not sent automatically. Completed visits wait on the{' '}
        <Link to="/money">Invoices</Link> screen for review before they are created in Xero.
      </p>
    </section>
  );
}

// ---- Importing Xero contacts ----

interface XeroContactRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  linked: boolean;
}

const ROLE_CHOICES: [string, string][] = [
  ['', "Don't import"],
  ['client', 'Client'],
  ['referrer', 'OT / rehab'],
  ['both', 'Client and OT / rehab'],
  ['contractor', 'Contractor'],
];

function XeroContactImport() {
  const status = useLoad(() => get<XeroStatus>('/api/accounting/xero'), []);
  if (!status.data?.connected) return null;
  return <XeroContactImportPanel />;
}

function XeroContactImportPanel() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<XeroContactRow[] | null>(null);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  async function load() {
    setLoading(true);
    setProblems([]);
    try {
      const r = await get<{ items: XeroContactRow[] }>('/api/accounting/xero/contacts');
      setItems(r.items);
      setSelections({});
      setOpen(true);
    } catch (err) {
      setProblems(describe(err).problems);
    } finally {
      setLoading(false);
    }
  }

  async function doImport() {
    const byId = new Map((items ?? []).map((c) => [c.id, c]));
    const selection = Object.fromEntries(
      Object.entries(selections)
        .filter(([, type]) => type !== '')
        .map(([id, type]) => [
          id,
          {
            type,
            name: byId.get(id)?.name ?? '',
            email: byId.get(id)?.email,
            phone: byId.get(id)?.phone,
          },
        ]),
    );
    if (Object.keys(selection).length === 0) {
      setProblems(['Choose at least one contact to import.']);
      return;
    }
    setProblems([]);
    try {
      const r = await post<{ created: number; skipped: string[] }>(
        '/api/accounting/xero/contacts/import',
        selection,
      );
      toast(
        r.created === 0
          ? 'Nothing was imported.'
          : `Imported ${r.created} contact${r.created === 1 ? '' : 's'}.`,
      );
      await load();
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  async function refresh() {
    setProblems([]);
    try {
      const r = await post<{ refreshed: number; filled: number }>(
        '/api/accounting/xero/contacts/refresh',
      );
      toast(`Refreshed ${r.refreshed} linked contact${r.refreshed === 1 ? '' : 's'}.`);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  const shown = (items ?? []).filter((c) => {
    const q = query.trim().toLowerCase();
    return q === '' || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });
  const selectedCount = Object.values(selections).filter((t) => t !== '').length;

  return (
    <section className="stack narrow" aria-labelledby="xero-import-h">
      <h2 id="xero-import-h">Import contacts from Xero</h2>
      {!open && <Problems problems={problems} title="Could not do that." />}
      <div className="row start">
        <button type="button" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : items ? 'Reload Xero contacts' : 'Load Xero contacts'}
        </button>
        <button type="button" onClick={() => void refresh()}>
          Refresh linked contacts
        </button>
      </div>
      {open && items && (
        <div className="backdrop">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="xero-import-modal-h"
            className="dialog wide"
            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          >
            <div className="stack">
              <h2 id="xero-import-modal-h">Choose contacts to import</h2>
              <Problems problems={problems} title="Could not do that." />
              {items.length === 0 ? (
                <p className="empty">No contacts in Xero.</p>
              ) : (
                <>
                  <div className="filters" role="search">
                    <label>
                      Search
                      <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label="Search Xero contacts"
                        autoFocus
                      />
                    </label>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Import as</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((c) => (
                        <tr key={c.id}>
                          <td data-label="Name">{c.name}</td>
                          <td data-label="Email">{c.email}</td>
                          <td data-label="Phone">{c.phone}</td>
                          <td data-label="Import as">
                            {c.linked ? (
                              <span className="pill ok">Already imported</span>
                            ) : (
                              <select
                                aria-label={`Import ${c.name} as`}
                                value={selections[c.id] ?? ''}
                                onChange={(e) =>
                                  setSelections({ ...selections, [c.id]: e.target.value })
                                }
                              >
                                {ROLE_CHOICES.map(([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              <div className="row end">
                <button type="button" className="quiet" onClick={() => setOpen(false)}>
                  Close
                </button>
                {items.length > 0 && (
                  <button
                    type="button"
                    onClick={() => void doImport()}
                    disabled={selectedCount === 0}
                  >
                    Import selected{selectedCount > 0 ? ` (${selectedCount})` : ''}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
